import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  Tabs,
  Tab
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { useUserPlaidStatus } from "./hooks/useUserPlaidStatus";
import { useGdprExportRequest } from "./hooks/useGdprExportRequest";
import { useGdprExportStatus } from "./hooks/useGdprExportStatus";
import { useResetMfa } from "./hooks/useResetMfa";
import PlaidTransactionModal from "./PlaidTransactionModal";
import { BudgetsSection } from "./BudgetsSection";
import { makeApiRequest } from "../utils";

import { useQueryClient } from "@tanstack/react-query";
import { SUPPORT_USER_PLAID_STATUS_QUERY_KEY } from "../../constants/queryKeys";

const SupportDashboard = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);
  const [searchInput, setSearchInput] = useState(userId || "");
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [modalState, setModalState] = useState({
    open: false,
    plaidItemId: null,
    plaidAccountId: null,
    accountLabel: ""
  });

  const { data, isLoading, error } = useUserPlaidStatus(userId);
  const { requestGdprExport } = useGdprExportRequest();
  const [gdprExportJobId, setGdprExportJobId] = useState(null);
  const { data: gdprExportStatus } = useGdprExportStatus(gdprExportJobId);
  const { resetMfa } = useResetMfa();

  useEffect(() => {
    if (userId) {
      setSearchInput(userId);
    }
  }, [userId]);

  const handleSearch = async () => {
    if (!searchInput.trim()) { return; }

    setIsSearching(true);
    setSearchError(null);
    try {
      const term = searchInput.trim();
      const params = new URLSearchParams({ search: term }).toString();

      const result = await makeApiRequest({
        endpoint: `/api/support/user-plaid-status?${params}`,
        method: "GET"
      });

      if (result && result.user && result.user.id) {
        queryClient.setQueryData(
          [SUPPORT_USER_PLAID_STATUS_QUERY_KEY, String(result.user.id)],
          result
        );
        navigate(`/support/${result.user.id}`);
      } else {
        setSearchError("User not found");
      }
    } catch (err) {
      setSearchError(
        err.data?.error || err.message || "Error searching for user"
      );
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleRequestGdprExport = () => {
    setGdprExportJobId(null);
    requestGdprExport.mutate(
      { userId },
      {
        onSuccess: (result) => setGdprExportJobId(result.jobId)
      }
    );
  };

  const handleResetMfa = () => {
    if (!window.confirm(
      "Reset MFA for this user? This disables MFA and clears their secret and backup codes."
    )) {
      return;
    }
    resetMfa.mutate({ userId });
  };

  const openModal = (plaidItemId, plaidAccountId, label) => {
    setModalState({
      open: true,
      plaidItemId,
      plaidAccountId,
      accountLabel: label
    });
  };

  const closeModal = () => {
    setModalState({
      open: false,
      plaidItemId: null,
      plaidAccountId: null,
      accountLabel: ""
    });
  };

  const buildAccountRows = (item) => {
    const rows = [];

    // Our DB accounts
    if (item.PlaidItemAccounts) {
      for (const acc of item.PlaidItemAccounts) {
        const plaidMatch = acc.plaidAccountId &&
          item.plaidAccountMatches?.[acc.plaidAccountId];
        const hasWarning = acc.plaidAccountId &&
          item.plaidAccountMatches &&
          !plaidMatch &&
          !item.plaidError;

        rows.push({
          key: `db-${acc.id}`,
          joroAccountName: acc.accountName,
          mask: acc.mask ? `****${acc.mask}` : "-",
          plaidAccountId: acc.plaidAccountId || "-",
          type: acc.accountType,
          subtype: acc.accountSubtype,
          status: acc.isDisabled ? "Disabled" : "Active",
          statusColor: acc.isDisabled ? "default" : "success",
          canCompare: !!acc.plaidAccountId,
          plaidItemId: item.id,
          plaidAccountIdRaw: acc.plaidAccountId,
          warning: hasWarning
            ? "Account not found in Plaid"
            : null,
          isUnmatched: false
        });
      }
    }

    // Unmatched Plaid-only accounts
    if (item.unmatchedPlaidAccounts) {
      for (const pa of item.unmatchedPlaidAccounts) {
        rows.push({
          key: `plaid-${pa.accountId}`,
          joroAccountName: null,
          mask: pa.mask ? `****${pa.mask}` : "-",
          plaidAccountId: pa.accountId,
          type: pa.type,
          subtype: pa.subtype,
          status: "-",
          statusColor: null,
          canCompare: true,
          plaidItemId: item.id,
          plaidAccountIdRaw: pa.accountId,
          warning: null,
          isUnmatched: true
        });
      }
    }

    return rows;
  };

  return (
    <Box sx={{ padding: 4 }}>
      <Typography variant="h4" gutterBottom>
        Support Dashboard
      </Typography>

      <Box sx={{ display: "flex", gap: 2, marginBottom: 4 }}>
        <TextField
          label="User Email or ID"
          variant="outlined"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={handleKeyPress}
          sx={{ width: 400 }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleSearch}
          disabled={isSearching || !searchInput.trim()}
        >
          {isSearching ? "Searching..." : "Search"}
        </Button>
      </Box>

      {searchError && (
        <Alert severity="error" sx={{ marginBottom: 4 }}>
          {searchError}
        </Alert>
      )}

      {!userId && !searchError && (
        <Typography color="textSecondary">
          Enter a user email or ID to search.
        </Typography>
      )}

      {userId && error && (
        <Alert severity="error" sx={{ marginBottom: 4 }}>
          {error.data || error.message || "Error fetching user data"}
        </Alert>
      )}

      {userId && isLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            my: 4
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {userId && data && (
        <Box>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{ marginBottom: 3 }}
          >
            <Tab label="Plaid" />
            <Tab label="Budgets" />
            <Tab label="Utilities" />
          </Tabs>

          {activeTab === 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 4
              }}
            >
              {/* User Info */}
              <Card>
                <CardContent>
                  <Typography variant="h6">
                    User Information
                  </Typography>
                  <Typography>
                    <strong>Name:</strong>{" "}
                    {data.user.firstName} {data.user.lastName}
                  </Typography>
                  <Typography>
                    <strong>Email:</strong> {data.user.email}
                  </Typography>
                  <Typography>
                    <strong>ID:</strong> {data.user.id}
                  </Typography>
                </CardContent>
              </Card>

              {/* Plaid Items & Accounts */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                Plaid Items & Accounts
                  </Typography>
                  {data.plaidItems.length === 0
                    ? (
                      <Typography color="textSecondary">
                    No Plaid Items found.
                      </Typography>
                    )
                    : (
                      data.plaidItems.map((item) => {
                        const accountRows = buildAccountRows(item);
                        return (
                          <Box
                            key={item.id}
                            sx={{
                              marginBottom: 3,
                              borderBottom: "1px solid #eee",
                              paddingBottom: 2
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                marginBottom: 1
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                fontWeight="bold"
                              >
                                {item.institutionName}
                              </Typography>
                              <Chip
                                label={
                                  item.itemLoginRequired
                                    ? "Login Required"
                                    : "Active"
                                }
                                color={
                                  item.itemLoginRequired
                                    ? "error"
                                    : "success"
                                }
                                size="small"
                              />
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                            ID: {item.id} | Inst ID:{" "}
                                {item.institutionId}
                              </Typography>
                            </Box>

                            {/* Plaid error alert */}
                            {item.plaidError && (
                              <Alert
                                severity="warning"
                                sx={{ mb: 1 }}
                              >
                                {item.plaidError}
                              </Alert>
                            )}

                            {/* Institution Status */}
                            {data.institutionStatuses
                              .filter(
                                (s) =>
                                  s.plaidInstitutionId ===
                              item.institutionId
                              )
                              .slice(0, 1)
                              .map((status) => (
                                <Box
                                  key={status.id}
                                  sx={{
                                    marginBottom: 1,
                                    backgroundColor: "#f9f9f9",
                                    padding: 1,
                                    borderRadius: 1
                                  }}
                                >
                                  <Typography variant="body2">
                                    <strong>
                                  Institution Status:
                                    </strong>{" "}
                                    {status.status || "Unknown"}{" "}
                                (Success: {status.success}%)
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    display="block"
                                  >
                                Last Updated:{" "}
                                    {new Date(
                                      status.lastStatusChangeAt ||
                                    status.createdAt
                                    ).toLocaleString()}
                                  </Typography>
                                </Box>
                              ))}

                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>
                                Joro Account Name
                                  </TableCell>
                                  <TableCell>Mask</TableCell>
                                  <TableCell>
                                Plaid Account ID
                                  </TableCell>
                                  <TableCell>Type</TableCell>
                                  <TableCell>Subtype</TableCell>
                                  <TableCell>Status</TableCell>
                                  <TableCell>Transactions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {accountRows.length > 0
                                  ? (
                                    accountRows.map((row) => (
                                      <TableRow
                                        key={row.key}
                                        sx={
                                          row.isUnmatched
                                            ? {
                                              backgroundColor:
                                              "#fff3e0"
                                            }
                                            : {}
                                        }
                                      >
                                        <TableCell>
                                          {row.isUnmatched
                                            ? (
                                              <Tooltip title="Plaid account not in our DB">
                                                <WarningAmberIcon
                                                  color="warning"
                                                  fontSize="small"
                                                />
                                              </Tooltip>
                                            )
                                            : row.joroAccountName}
                                        </TableCell>
                                        <TableCell>
                                          {row.mask}
                                        </TableCell>
                                        <TableCell>
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems:
                                            "center",
                                              gap: 0.5
                                            }}
                                          >
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                fontSize:
                                              "0.75rem",
                                                fontFamily:
                                              "monospace"
                                              }}
                                            >
                                              {row.plaidAccountId}
                                            </Typography>
                                            {row.warning && (
                                              <Tooltip
                                                title={
                                                  row.warning
                                                }
                                              >
                                                <WarningAmberIcon
                                                  color="warning"
                                                  fontSize="small"
                                                />
                                              </Tooltip>
                                            )}
                                          </Box>
                                        </TableCell>
                                        <TableCell>
                                          {row.type}
                                        </TableCell>
                                        <TableCell>
                                          {row.subtype}
                                        </TableCell>
                                        <TableCell>
                                          {row.statusColor
                                            ? (
                                              <Chip
                                                label={row.status}
                                                color={
                                                  row.statusColor
                                                }
                                                size="small"
                                              />
                                            )
                                            : row.status}
                                        </TableCell>
                                        <TableCell>
                                          <IconButton
                                            size="small"
                                            disabled={
                                              !row.canCompare
                                            }
                                            onClick={() =>
                                              openModal(
                                                row.plaidItemId,
                                                row.plaidAccountIdRaw,
                                                `${row.joroAccountName || "Plaid Account"} ${row.mask}`
                                              )
                                            }
                                          >
                                            <ReceiptLongIcon
                                              fontSize="small"
                                            />
                                          </IconButton>
                                        </TableCell>
                                      </TableRow>
                                    ))
                                  )
                                  : (
                                    <TableRow>
                                      <TableCell
                                        colSpan={7}
                                        align="center"
                                      >
                                    No accounts linked to this
                                    item.
                                      </TableCell>
                                    </TableRow>
                                  )}
                              </TableBody>
                            </Table>
                          </Box>
                        );
                      })
                    )}
                </CardContent>
              </Card>

              {/* Plaid Sessions */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                Recent Plaid Sessions
                  </Typography>
                  {data.plaidSessions.length === 0
                    ? (
                      <Typography color="textSecondary">
                    No session logs found.
                      </Typography>
                    )
                    : (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Timestamp</TableCell>
                            <TableCell>Event Type</TableCell>
                            <TableCell>Event Name</TableCell>
                            <TableCell>Institution</TableCell>
                            <TableCell>Link Session ID</TableCell>
                            <TableCell>Request ID</TableCell>
                            <TableCell>Error / Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.plaidSessions.map((session) => (
                            <TableRow key={session.id}>
                              <TableCell>
                                {new Date(
                                  session.timestamp
                                ).toLocaleString()}
                              </TableCell>
                              <TableCell>
                                {session.eventType}
                              </TableCell>
                              <TableCell>
                                {session.eventName}
                              </TableCell>
                              <TableCell>
                                {session.institutionName || "-"}
                              </TableCell>
                              <TableCell>
                                {session.linkSessionId || "-"}
                              </TableCell>
                              <TableCell>
                                {session.requestId || "-"}
                              </TableCell>
                              <TableCell>
                                {session.errorCode
                                  ? (
                                    <Typography
                                      color="error"
                                      variant="body2"
                                    >
                                      {session.errorCode}
                                    </Typography>
                                  )
                                  : session.exitStatus
                                    ? (
                                      <Typography variant="body2">
                                    Exit: {session.exitStatus}
                                      </Typography>
                                    )
                                    : "-"}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                </CardContent>
              </Card>

              {/* Spending Aggregations */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                Spending Aggregations
                  </Typography>
                  {data.usersSpendingAggregations.length === 0
                    ? (
                      <Typography color="textSecondary">
                    No spending aggregations found.
                      </Typography>
                    )
                    : (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Preset</TableCell>
                            <TableCell>Type</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.usersSpendingAggregations.map((row) => (
                            <TableRow key={row.SpendingAggregation.id}>
                              <TableCell>
                                {row.SpendingAggregation.id}
                              </TableCell>
                              <TableCell>
                                {row.SpendingAggregation.name}
                              </TableCell>
                              <TableCell>
                                {row.SpendingAggregation.ownerId == null
                                  ? "Yes"
                                  : "No"}
                              </TableCell>
                              <TableCell>
                                {row.SpendingAggregation.type}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                </CardContent>
              </Card>
            </Box>
          )}

          {activeTab === 1 && <BudgetsSection userId={userId} />}

          {activeTab === 2 && (
            <Card>
              <CardContent>
                <Typography variant="h6">
                  Utilities
                </Typography>
                <Box sx={{ marginTop: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleRequestGdprExport}
                    disabled={
                      requestGdprExport.isLoading ||
                      (gdprExportStatus &&
                        !["completed", "failed"].includes(
                          gdprExportStatus.state
                        ))
                    }
                  >
                    Request GDPR Export
                  </Button>

                  {requestGdprExport.isError && (
                    <Alert severity="error" sx={{ marginTop: 1 }}>
                      {requestGdprExport.error?.data ||
                        requestGdprExport.error?.message ||
                        "Error requesting GDPR export"}
                    </Alert>
                  )}

                  {gdprExportStatus &&
                    !["completed", "failed"].includes(
                      gdprExportStatus.state
                    ) && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        marginTop: 1
                      }}
                    >
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="textSecondary">
                        Export {gdprExportStatus.state}...
                      </Typography>
                    </Box>
                  )}

                  {gdprExportStatus?.state === "completed" && (
                    <Alert severity="success" sx={{ marginTop: 1 }}>
                      Export ready ({gdprExportStatus.tableCount} tables).{" "}
                      <a
                        href={gdprExportStatus.downloadUrl}
                        rel="noopener noreferrer"
                      >
                        Download
                      </a>
                      {gdprExportStatus.erroredTables?.length > 0 && (
                        <Typography variant="body2" color="error">
                          Failed to export:{" "}
                          {gdprExportStatus.erroredTables
                            .map((t) => t.table)
                            .join(", ")}
                        </Typography>
                      )}
                    </Alert>
                  )}

                  {gdprExportStatus?.state === "failed" && (
                    <Alert severity="error" sx={{ marginTop: 1 }}>
                      Export failed: {gdprExportStatus.failedReason}
                    </Alert>
                  )}
                </Box>

                <Box sx={{ marginTop: 3 }}>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleResetMfa}
                    disabled={resetMfa.isLoading}
                  >
                    Reset MFA
                  </Button>

                  {resetMfa.isError && (
                    <Alert severity="error" sx={{ marginTop: 1 }}>
                      {resetMfa.error?.data ||
                        resetMfa.error?.message ||
                        "Error resetting MFA"}
                    </Alert>
                  )}

                  {resetMfa.isSuccess && (
                    <Alert severity="success" sx={{ marginTop: 1 }}>
                      MFA reset for this user.
                    </Alert>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      <PlaidTransactionModal
        open={modalState.open}
        onClose={closeModal}
        plaidItemId={modalState.plaidItemId}
        plaidAccountId={modalState.plaidAccountId}
        accountLabel={modalState.accountLabel}
      />
    </Box>
  );
};

export default SupportDashboard;
